/**
 * Language codes that require the non-Latin fonts bundled in fonts-special.
 * Values are the exact strings returned by TaskInfo.getISOLanguageCode() /
 * getISOInputLanguageCode() — not BCP-47 (so 'ur_UR', not 'ur-PK'). Tested
 * against either the assessment language or the candidate's input language;
 * a match in either triggers the bundle download.
 */
const SPECIALTY_FONT_LANGUAGES = new Set([
    'ur_UR', // Urdu       — Jameel Noori Nastaleeq
    'ar_AR', // Arabic     — Tajawal
    'hi_HI'  // Hindi      — Lohit Devanagari
]);

/**
 * Substrings (case-insensitive) in taskInfo.getSubject() that mark an
 * assessment as needing the GeoGebra applet bundle. The backend subject field
 * is free-form — real values include strings like "Mathematics Higher Level",
 * "MATH_HL", "Physics SL" — so we match on substrings rather than exact codes.
 */
const GEOGEBRA_SUBJECT_MATCHES = ['math', 'physics'];

/**
 * Substrings (case-insensitive) in taskInfo.getSubject() that mark an
 * assessment as needing the 'tools' preload bundle (wiris equation editor,
 * universal-canvas drawing tool, measurement-tool iframe). Matched the same
 * free-form substring way as GEOGEBRA_SUBJECT_MATCHES. The starting list is
 * deliberately broad — all STEM subjects — because wiris (equations) and the
 * measurement-tool (geometry) are in practice used across the full STEM
 * spectrum. Non-STEM candidates skip the ~8-10 MB tools download entirely
 * and fall back to network fetches for the rare edge case where their
 * assessment does include a drawing-based response.
 */
const TOOLS_SUBJECT_MATCHES = [
    'math',
    'physics',
    'chemistry',
    'biology',
    'scien',       // matches Science, Sciences, Social Sciences, Computer Science, etc.
    'geography',
    'integrated'   // matches Integrated Sciences / Integrated Humanities
];

/**
 * IAAPreferencesService is a service class responsible for managing and persisting user preferences.
 * It integrates with various services and signals to apply and update preferences dynamically.
 */
class IAAPreferencesService {
    /**
     * Constructor for IAAPreferencesService.
     * @param {Object} $bridges - Service for bridge operations.
     * @param {Object} $rootScope - AngularJS root scope.
     * @param {Object} $signals - Service for signal operations.
     * @param {Object} $translate - Service for translation.
     * @param {Object} TaskCountdownService - Service for task countdown.
     * @param {Object} ColorThemeService - Service for color theme management.
     * @param {Object} TaskInfoService - Service for task information.
     * @param {Object} PreloadBridgeService - Preload bridge for on-demand bundles.
     */
    constructor($bridges, $rootScope, $signals, $translate, TaskCountdownService, ColorThemeService, TaskInfoService, PreloadBridgeService) {
        this.$bridges = $bridges;
        this.$rootScope = $rootScope;
        this.$signals = $signals;
        this.$translate = $translate;
        this.TaskCountdownService = TaskCountdownService;
        this.ColorThemeService = ColorThemeService;
        this.TaskInfoService = TaskInfoService;
        this.PreloadBridgeService = PreloadBridgeService;

        this.RESPONSE_KEY_IAAPREFS = 'iaaPrefs';
        this.$logger = $bridges.logger();
        this.initialized = false;
        this.canPersist = false;
        this.iaaPreferencesWatchFnDereg = null;
        this.$eventLog = $bridges.eventLog();
        this.eventExplanation = {};
        this.notAllowedLogging = [
            'zoomLevel',
            'dyslexicFont',
            'extraTime'
        ];

        // Session flags for the fonts-special bundle request.
        // - _specialtyFontsEnsured latches only AFTER the service worker
        //   confirms the bundle is ready. A failed or dropped request leaves
        //   it false so the next trigger (preference change, reload) can retry.
        // - _specialtyFontsInFlight gates the "don't fire a second request
        //   while one is still pending" check. Cleared by the status listener
        //   installed in init().
        this._specialtyFontsEnsured = false;
        this._specialtyFontsInFlight = false;
        this._specialtyFontsStatusHandler = null;

        // Session flags for the geogebra bundle request. Mirror the
        // fonts-special flags — latched only when the service worker confirms
        // readiness; failures reset inFlight so the next trigger can retry.
        // The status handler itself is shared with fonts-special — see
        // _ensurePreloadStatusListener — so there is no separate handler field.
        this._geogebraEnsured = false;
        this._geogebraInFlight = false;

        // Session flags for the on-demand tools bundle request. Same pattern
        // as geogebra/fonts-special — a single status handler dispatches on
        // ensuredBundles membership.
        this._toolsEnsured = false;
        this._toolsInFlight = false;

        this._logIdentifier = 'IAAPreferencesService';
        this.$logger.logEnter(this, 'Init');
    }

    /**
     * Whether the current assessment or preferences require the optional
     * fonts-special preload bundle (non-Latin scripts or dyslexia preference).
     *
     * @param {Object} taskInfo - A TaskInfo instance (from TaskInfoService)
     * @param {Object} preferences - The iaaPreferences dict
     * @returns {boolean}
     */
    requiresSpecialtyFonts(taskInfo, preferences) {
        if (preferences && preferences.dyslexicFont === true) {
            return true;
        }
        if (!taskInfo) {
            return false;
        }
        // Check both the assessment presentation language and the candidate's
        // input language — an English assessment with Urdu input still needs
        // the Jameel font.
        const assessmentLang = typeof taskInfo.getISOLanguageCode === 'function'
            ? taskInfo.getISOLanguageCode()
            : null;
        const inputLang = typeof taskInfo.getISOInputLanguageCode === 'function'
            ? taskInfo.getISOInputLanguageCode()
            : null;
        return SPECIALTY_FONT_LANGUAGES.has(assessmentLang) ||
               SPECIALTY_FONT_LANGUAGES.has(inputLang);
    }

    /**
     * If the current assessment/preferences require specialty fonts, ask the
     * preload bridge to fetch the fonts-special bundle.
     *
     * The latch flag is set only after the service worker broadcasts a
     * matching ready status (see the listener wired up in _initPreloadStatusListener).
     * A transient failure leaves the flag false so the next trigger (preference
     * change, re-entry) can retry. If the preload bridge is unavailable (native
     * mode or no service worker controller yet), this is a no-op — the missing
     * fonts will be fetched over the network via the browser's normal CSS flow.
     */
    _ensureSpecialtyFontsIfNeeded() {
        if (this._specialtyFontsEnsured) return;
        if (this._specialtyFontsInFlight) return;
        if (!this.PreloadBridgeService || !this.PreloadBridgeService.isAvailable()) return;

        this.TaskInfoService.loadTaskInfo(function(taskInfo) {
            try {
                if (this._specialtyFontsEnsured || this._specialtyFontsInFlight) return;
                if (!this.requiresSpecialtyFonts(taskInfo, this.getPreferences())) return;

                this._specialtyFontsInFlight = true;
                this._ensurePreloadStatusListener();

                this.$logger.info(this, 'Requesting fonts-special preload bundle');
                this.PreloadBridgeService.ensureBundles(['fonts-special']);
            } catch (err) {
                this._specialtyFontsInFlight = false;
                this.$logger.error(this, 'Error while requesting fonts-special', err);
            }
        }.bind(this));
    }

    /**
     * Whether the current assessment needs the GeoGebra preload bundle.
     * Matched against taskInfo.getSubject() — returns true when the subject
     * contains any GEOGEBRA_SUBJECT_MATCHES substring (case-insensitive).
     *
     * @param {Object} taskInfo - A TaskInfo instance (from TaskInfoService)
     * @returns {boolean}
     */
    requiresGeogebra(taskInfo) {
        if (!taskInfo || typeof taskInfo.getSubject !== 'function') {
            return false;
        }
        const subject = taskInfo.getSubject();
        if (typeof subject !== 'string' || subject === '') {
            return false;
        }
        const lower = subject.toLowerCase();
        return GEOGEBRA_SUBJECT_MATCHES.some(function(match) {
            return lower.indexOf(match) !== -1;
        });
    }

    /**
     * Whether the current assessment needs the on-demand tools bundle (wiris
     * equation editor, universal-canvas drawing tool, measurement-tool
     * iframe). Matched against taskInfo.getSubject() — returns true when the
     * subject contains any TOOLS_SUBJECT_MATCHES substring (case-insensitive).
     *
     * @param {Object} taskInfo
     * @returns {boolean}
     */
    requiresTools(taskInfo) {
        if (!taskInfo || typeof taskInfo.getSubject !== 'function') {
            return false;
        }
        const subject = taskInfo.getSubject();
        if (typeof subject !== 'string' || subject === '') {
            return false;
        }
        const lower = subject.toLowerCase();
        return TOOLS_SUBJECT_MATCHES.some(function(match) {
            return lower.indexOf(match) !== -1;
        });
    }

    /**
     * If the current assessment is a STEM subject, ask the preload bridge to
     * fetch the tools bundle. Idempotent via _toolsEnsured.
     */
    _ensureToolsIfNeeded() {
        if (this._toolsEnsured) return;
        if (this._toolsInFlight) return;
        if (!this.PreloadBridgeService || !this.PreloadBridgeService.isAvailable()) return;

        this.TaskInfoService.loadTaskInfo(function(taskInfo) {
            try {
                if (this._toolsEnsured || this._toolsInFlight) return;
                if (!this.requiresTools(taskInfo)) return;

                this._toolsInFlight = true;
                this._ensurePreloadStatusListener();

                this.$logger.info(this, 'Requesting tools preload bundle');
                this.PreloadBridgeService.ensureBundles(['tools']);
            } catch (err) {
                this._toolsInFlight = false;
                this.$logger.error(this, 'Error while requesting tools', err);
            }
        }.bind(this));
    }

    /**
     * If the current assessment is a math or physics subject, ask the preload
     * bridge to fetch the geogebra bundle.
     *
     * Mirrors the fonts-special flow: idempotent via _geogebraEnsured, gated
     * on bridge availability (no-op in native mode), and cleared on failure
     * so a re-entry can retry.
     */
    _ensureGeogebraIfNeeded() {
        if (this._geogebraEnsured) return;
        if (this._geogebraInFlight) return;
        if (!this.PreloadBridgeService || !this.PreloadBridgeService.isAvailable()) return;

        this.TaskInfoService.loadTaskInfo(function(taskInfo) {
            try {
                if (this._geogebraEnsured || this._geogebraInFlight) return;
                if (!this.requiresGeogebra(taskInfo)) return;

                this._geogebraInFlight = true;
                this._ensurePreloadStatusListener();

                this.$logger.info(this, 'Requesting geogebra preload bundle');
                this.PreloadBridgeService.ensureBundles(['geogebra']);
            } catch (err) {
                this._geogebraInFlight = false;
                this.$logger.error(this, 'Error while requesting geogebra', err);
            }
        }.bind(this));
    }

    /**
     * Lazily attach a listener to the preload status channel that reacts to
     * terminal results of targeted ensureBundles() calls. Idempotent — the
     * listener is attached at most once per service lifetime.
     */
    _ensurePreloadStatusListener() {
        if (this._specialtyFontsStatusHandler) return;
        if (!this.PreloadBridgeService || !this.PreloadBridgeService.isAvailable()) return;

        var self = this;
        // Single handler dispatches on ensuredBundles membership so both the
        // fonts-special and geogebra ensures share one subscription.
        this._specialtyFontsStatusHandler = function(status) {
            if (!status || !status.ensuredBundles) return;
            // PWAPreloadBridgeClient replays the last cached status synchronously
            // when a new subscriber registers. If we're not actually waiting for
            // a given bundle, a replayed 'ready' from a previous session would
            // spuriously latch the flag, making the next manifest rotation
            // invisible for the rest of the tab's lifetime. Only act on a
            // bundle when we have an outstanding request for it.
            // Three terminal states reach this listener:
            //   - 'ready': bundle is stored — latch ensured + clear inFlight.
            //   - 'error': bundle failed — clear inFlight so a later call can
            //     retry; warn (not error) so the candidate is not interrupted
            //     by the ErrorHandler dialog (preload is a background
            //     optimization with on-demand network fallback).
            //   - 'idle' + ensuredBundles: the ensure was short-circuited by a
            //     designed gate (dev-mode skip, awaiting school activation) —
            //     clear inFlight so a retry after the gate lifts (e.g. after
            //     activateForSchool) is not silently suppressed. Do NOT latch
            //     ensured, since nothing was actually downloaded.
            if (status.ensuredBundles.indexOf('fonts-special') !== -1 && self._specialtyFontsInFlight) {
                if (status.state === 'ready') {
                    self._specialtyFontsEnsured = true;
                    self._specialtyFontsInFlight = false;
                    self.$logger.info(self, 'fonts-special preload bundle ready');
                } else if (status.state === 'error') {
                    self._specialtyFontsInFlight = false;
                    self.$logger.warn(
                        self,
                        'fonts-special preload failed: ' + (status.error || 'unknown')
                    );
                } else if (status.state === 'idle') {
                    self._specialtyFontsInFlight = false;
                    self.$logger.info(self, 'fonts-special preload deferred (gated)');
                }
            }
            if (status.ensuredBundles.indexOf('geogebra') !== -1 && self._geogebraInFlight) {
                if (status.state === 'ready') {
                    self._geogebraEnsured = true;
                    self._geogebraInFlight = false;
                    self.$logger.info(self, 'geogebra preload bundle ready');
                } else if (status.state === 'error') {
                    self._geogebraInFlight = false;
                    self.$logger.warn(
                        self,
                        'geogebra preload failed: ' + (status.error || 'unknown')
                    );
                } else if (status.state === 'idle') {
                    self._geogebraInFlight = false;
                    self.$logger.info(self, 'geogebra preload deferred (gated)');
                }
            }
            if (status.ensuredBundles.indexOf('tools') !== -1 && self._toolsInFlight) {
                if (status.state === 'ready') {
                    self._toolsEnsured = true;
                    self._toolsInFlight = false;
                    self.$logger.info(self, 'tools preload bundle ready');
                } else if (status.state === 'error') {
                    self._toolsInFlight = false;
                    self.$logger.warn(
                        self,
                        'tools preload failed: ' + (status.error || 'unknown')
                    );
                } else if (status.state === 'idle') {
                    self._toolsInFlight = false;
                    self.$logger.info(self, 'tools preload deferred (gated)');
                }
            }
        };
        this.PreloadBridgeService.onStatusUpdate(this._specialtyFontsStatusHandler);
    }

    /**
     * Initializes the service. Can force reinitialization if needed.
     * @param {boolean} [force=false] - Force reinitialization.
     */
    init(force) {
        // SMELL: Redundant logging and checks can be optimized.
        var _force = angular.isDefined(force) ? force : false;
        if (this.initialized && !_force) {
            this.$logger.info(this, "Already initialized");
            return;
        }

        this.$logger.info(this, "initializing");
        this.$rootScope.iaaPreferences = {};

        if (angular.isFunction(this.iaaPreferencesWatchFnDereg)) {
            this.$logger.info(this, "deregistering old watch");
            this.iaaPreferencesWatchFnDereg();
        }

        var iaaPreferencesWatchFn = function(newVal, oldVal) {
            for (var key in this.$rootScope.iaaPreferences) {
                var message = '';

                if (newVal != oldVal && this.notAllowedLogging.indexOf(key) === -1) {
                    if (oldVal[key] != newVal[key]) {
                        if (typeof newVal[key] === 'boolean') {
                            message = this.$translate.instant(newVal[key] ? 'IAA.activated' : 'IAA.deactivated', { 'key' : this.$translate.instant('IAA.'+key) } );
                        } else {
                            var translationKey = !angular.isDefined(oldVal[key]) || oldVal[key] === '' ?
                                'IAA.changedValueNoPreviousValue'
                                :
                                'IAA.changedValue';

                            message = this.$translate.instant(translationKey, {
                                'key': this.$translate.instant('IAA.' + key).toLowerCase(),
                                'oldVal': oldVal[key] !== '00' ? oldVal[key] : 'Default',
                                'newVal': newVal[key] !== '00' ? newVal[key] : 'Default'
                            });
                        }

                        var metaData = {};
                        metaData[key] = newVal[key];
                        this.$eventLog.logEvent(
                            message,
                            key,
                            this.eventExplanation[key] ? this.eventExplanation[key] : '',
                            "iaa",
                            metaData
                        );
                        this.eventExplanation[key] = '';
                    }
                }
                this.updatePreference(key, this.$rootScope.iaaPreferences[key]);
            }
            this.persistIAASettings();
        }.bind(this);

        this.$logger.info(this, "deregistering new watch");
        this.iaaPreferencesWatchFnDereg = this.$rootScope.$watch('iaaPreferences', iaaPreferencesWatchFn, true);

        this.$signals.on(IB.Signals.USER_FILE_LOADED, function(candidate) {
            this.applyIAAPreSettings(candidate);
        }.bind(this));

        this.$signals.on(IB.Signals.RESPONSE_INITIALIZED, function(hasExistingData) {
            this.canPersist = true;
            if (!hasExistingData) {
                return;
            }

            this.applyPersistedIAASettings();
            this.persistIAASettings();
        }.bind(this));

        this.$signals.on(IB.Signals.UPDATE_ZOOM_LEVEL, function(operator) {
            console.info("Change zoom1", operator);
            var iaaPrefs = this.$rootScope.iaaPreferences;
            if (!angular.isDefined(iaaPrefs['zoomLevel'])) {
                iaaPrefs['zoomLevel'] = 100;
            }
            if (operator == '+') {
                iaaPrefs['zoomLevel'] = iaaPrefs['zoomLevel'] + 5;
            } else if (operator == '-') {
                iaaPrefs['zoomLevel'] = iaaPrefs['zoomLevel'] - 5;
            }
            this.$rootScope.iaaPreferences = iaaPrefs;
        }.bind(this));

        this.initialized = true;
    }

    /**
     * Sets an explanation for an event.
     * @param {string} key - The key for the event.
     * @param {string} message - The explanation message.
     */
    setEventExplanation(key, message) {
        this.eventExplanation[key] = message;
    }

    /**
     * Applies candidate IAA settings (pre-defined by IBIS).
     * @param {Object} candidate - The candidate object.
     */
    applyIAAPreSettings(candidate) {
        if (!angular.isFunction(candidate.getIAA)) {
            this.$logger.warn(this, "Skipping IAA presettings, current user doesn't have IAA defined");
            return;
        }
        this.$logger.info(this, "Applying IAA PreSettings", JSON.stringify(candidate.getIAA()));
        this.updatePreferenceByDict(candidate.getIAA());
        this.$logger.info(this, "IAA dict applied: ", JSON.stringify(this.$rootScope.iaaPreferences));

        // applyIAAPreSettings runs on USER_FILE_LOADED, right after the
        // candidate record lands and before the assessment UI renders. This
        // is the earliest point at which TaskInfoService.loadTaskInfo can
        // return a populated TaskInfo, so it's the earliest point at which
        // requiresSpecialtyFonts() / requiresGeogebra() / requiresTools()
        // can make their language- and subject-based decisions.
        this._ensureSpecialtyFontsIfNeeded();
        this._ensureGeogebraIfNeeded();
        this._ensureToolsIfNeeded();
    }

    /**
     * Applies IAA settings from a pre-existing response.
     */
    applyPersistedIAASettings() {
        this.$logger.info(this, "Applying previously persisted IAA settings");
        var iaaPrefsData = this.$bridges.data().get(this.RESPONSE_KEY_IAAPREFS);
        if (!angular.isString(iaaPrefsData) && !angular.isObject(iaaPrefsData)) {
            this.$logger.info(this, "No previous IAA settings found");
            return;
        }
        var iaaPrefsParsed = {};
        if (angular.isObject(iaaPrefsData)) {
            iaaPrefsParsed = iaaPrefsData;
        } else if (angular.isString(iaaPrefsData)) {
            iaaPrefsParsed = JSON.parse(iaaPrefsData);
        }

        this.updatePreferenceByDict(iaaPrefsParsed);
    }

    /**
     * Persists IAA settings to candidate response.
     */
    persistIAASettings() {
        if (!this.canPersist) {
            return;
        }
        this.$logger.info(this, "Persisting IAA Settings", this.getPreferences());
        this.$bridges.data().put(this.RESPONSE_KEY_IAAPREFS, this.getPreferences());
    }

    /**
     * Updates preferences based on a dictionary.
     * @param {Object} dict - The dictionary containing preference key-value pairs.
     */
    updatePreferenceByDict(dict) {
        angular.forEach(dict, function(value, key) {
            if (key == 'extratime') {
                this.setIAAExtratime(value);
                return;
            }
            if (key == 'colorTheme') {
                this.$rootScope.iaaPreferences[key] = value;
                return;
            }

            this.$rootScope.iaaPreferences[key] = key !== 'alternativeContent' && value && (typeof value === 'string') ? this.$rootScope.$eval(value) : value;
        }.bind(this));
    }

    /**
     * Gets a preference by key.
     * @param {string} key - The preference key.
     * @returns {any} The preference value, or undefined if preferences not initialized.
     */
    getPreference(key) {
        if (!this.$rootScope.iaaPreferences) {
            return undefined;
        }
        return this.$rootScope.iaaPreferences[key];
    }

    /**
     * Gets all preferences.
     * @returns {Object} The preferences object, or empty object if not initialized.
     */
    getPreferences() {
        return this.$rootScope.iaaPreferences || {};
    }

    /**
     * Updates a preference.
     * @param {string} key - The preference key.
     * @param {any} val - The new value for the preference.
     */
    updatePreference(key, val) {
        this.$logger.info(this, 'IAA preference "' + key + '" changed to: ' + val);
        this.TaskInfoService.loadTaskInfo(function(taskInfo) {
            this.$rootScope.isUrdu = taskInfo.getISOInputLanguageCode() === 'ur_UR';
        }.bind(this));
        switch (key) {
            case 'zoomLevel':
                if (val < 100) {
                    val = 100;
                    this.$rootScope.iaaPreferences['zoomLevel'] = val;
                }
                if (val > 150) {
                    val = 150;
                    this.$rootScope.iaaPreferences['zoomLevel'] = val;
                }
                document.body.style.zoom = val / 100;
                break;
            case 'dyslexicFont':
                if (val === true && !this.$rootScope.isUrdu) {
                    $('style#dyslexiaCss').html('*:not(.mathjax):not(.MathJax):not([id^="MathJax"]):not(.noDyslexicFont):not(.glyphicon):not([class^="dcg-icon-"]), input:not(.mathjax), textarea:not(.mathjax) { font-family:"lexia_readable"!important;}');
                } else {
                    $('style#dyslexiaCss').html('');
                }
                if (val === true) {
                    // Toggling dyslexia on may be the first signal that we
                    // need Lexia — fetch the fonts-special bundle if we
                    // haven't already.
                    this._ensureSpecialtyFontsIfNeeded();
                }
                break;
            case 'candidateCanPause':
                // no need for action here
                break;
            case 'colorTheme':
                this.ColorThemeService.setCurrentColorTheme(val);
                break;
        }
    }

    /**
     * Sets extra time for the candidate.
     * @param {number} number_of_minutes - The number of minutes to set as extra time.
     */
    setIAAExtratime(number_of_minutes) {
        this.$logger.info(this, 'Setting extratime from IAA settings to ' + number_of_minutes + ' minutes.');
        this.TaskCountdownService.getInstance().setTotalExtraTimeMS(number_of_minutes * 60000, true); // set milliseconds
    }
}

export default IAAPreferencesService;